import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { PATHS } from '@client/routes';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';

import { Model } from './collections.viewModel';
import { DuplicatedCollection } from './collection.query';
import { ProgramDuplicatedToast } from './components/programDuplicatedToast';

export type DuplicateProgramActionMap = {
  DuplicateProgram: number;
  ClearError: null;
};

export type ProgramDuplicationAction = Actions<DuplicateProgramActionMap>;

export type DuplicateVM = {
  hasDuplicationFailed: boolean;
} & Dispatchers<DuplicateProgramActionMap>;

const notifyService = (p: {
  type: 'duplicated';
  value: DuplicatedCollection & { title: string };
}) => {
  if (p.type === 'duplicated') {
    toast((t) => (
      <ProgramDuplicatedToast
        name={p.value.title}
        programURL={PATHS.PROGRAM_EDIT(p.value.id, p.value.influencerId)}
        onClose={() => toast.dismiss(t.id)}
      />
    ));
  }
};

const findProgram = (p: Model['collections'], programId: number) => {
  const find = <T extends { programId: number }>(ps: Array<T>) =>
    ps.find((p) => p.programId === programId);
  return find(p.published) || find(p.draft) || find(p.archived);
};

export const useCollectionDuplicationVM = (
  model: Async<Model>,
): Async<DuplicateVM> => {
  const [selectedCollection, setSelectedCollection] = useState<number>(0);
  const [hasDuplicationFailed, setDuplicationFailed] = useState(false);

  useEffect(() => {
    if (model.type !== 'fetched') return;
    if (!selectedCollection) return;
    const d = model.value.programStateMap[
      'duplicateProgram'
    ] as unknown as Async<DuplicatedCollection>;
    if (d.type === 'fetch-error') return setDuplicationFailed(true);
    if (d.type !== 'fetched') return;
    const program = findProgram(model.value.collections, selectedCollection);
    if (!program) return;

    notifyService({
      type: 'duplicated',
      value: {
        ...d.value,
        title: program.title,
      },
    });

    setSelectedCollection(0);
  }, [model, selectedCollection]);

  const _dispatch = (a: ProgramDuplicationAction) => {
    trackSessionEvent('ViewAction', a);

    if (model.type !== 'fetched') return;
    switch (a.type) {
      case 'DuplicateProgram': {
        setDuplicationFailed(false);
        if (a.payload !== 0) setSelectedCollection(a.payload);

        return model.value.dispatch({
          type: 'duplicateProgram',
          payload: a.payload !== 0 ? a.payload : selectedCollection,
        });
      }
      case 'ClearError': {
        setDuplicationFailed(false);
        setSelectedCollection(0);
        return;
      }
    }
  };

  const dispatchers = getDispatchers<DuplicateProgramActionMap>(_dispatch);

  return toAsyncFetched({
    hasDuplicationFailed,
    ...dispatchers,
  });
};
